import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Hospital, BedDouble, LogOut, Receipt, Package,
         Stethoscope, RefreshCw, AlertTriangle, Building2, Eye, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NewAdmissionModal from "@/components/ipd/NewAdmissionModal";
import NewWardModal from "@/components/ipd/NewWardModal";
import NewBedModal from "@/components/ipd/NewBedModal";
import NewBillingModal from "@/components/ipd/NewBillingModal";
import NewInventoryModal from "@/components/ipd/NewInventoryModal";
import RestockModal from "@/components/ipd/RestockModal";
import BillPreviewModal from "@/components/ipd/BillPreviewModal";
import PendingAdmissionsPanel from "@/components/ipd/PendingAdmissionsPanel";
import CollectPaymentModal from "@/components/ipd/CollectPaymentModal";
import ABDMConsentsPanel from "@/components/ipd/ABDMConsentsPanel";
import DischargeSummaryModal from "@/components/ipd/DischargeSummaryModal";

// ---------- Tab 1: Active Admissions ----------
function ActiveAdmissions() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payFor, setPayFor] = useState(null);
  const [summaryFor, setSummaryFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ipd/admissions?status=active");
      setRows(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load admissions"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const view = async (id) => {
    try {
      const { data } = await api.get(`/ipd/admissions/${id}`);
      setDetail(data);
    } catch { toast.error("Failed to load details"); }
  };

  return (
    <div className="space-y-3" data-testid="ipd-tab-admissions">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{rows.length} active admission(s)</p>
        <div className="flex gap-2">
          <NewAdmissionModal onCreated={load} />
          <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>Doctor</TableHead>
            <TableHead>Ward</TableHead>
            <TableHead>Bed</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Admitted</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-400">Loading…</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-400">No active admissions</TableCell></TableRow>
          ) : rows.map((r) => (
            <TableRow key={r.id} data-testid={`ipd-admission-row-${r.id}`}>
              <TableCell className="font-medium">
                {r.patient_name || <span className="text-slate-400">—</span>}
                {r.patient_mobile && <span className="text-xs text-slate-500 block">{r.patient_mobile}</span>}
              </TableCell>
              <TableCell>{r.doctor_name || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell>{r.ward_name || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell>{r.bed_number || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell className="max-w-xs truncate">{r.reason_for_admission}</TableCell>
              <TableCell>{r.admission_date}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" onClick={() => setPayFor(r)}
                          data-testid={`ipd-collect-payment-${r.id}`}
                          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                    ₹ Collect
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setSummaryFor(r.id)}
                          data-testid={`ipd-summary-${r.id}`}
                          className="text-blue-700 border-blue-200 hover:bg-blue-50">
                    Summary
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => view(r.id)}
                          data-testid={`ipd-view-${r.id}`}>View</Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Admission details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Reason:</span> {detail.admission?.reason_for_admission}</p>
              <p><span className="font-medium">Chief complaint:</span> {detail.admission?.chief_complaint || "—"}</p>
              <p><span className="font-medium">LOS (days):</span> {detail.admission?.length_of_stay_days ?? 0}</p>
              <p><span className="font-medium">Daily notes:</span> {detail.daily_notes?.length || 0}</p>
              <p><span className="font-medium">Discharge summary:</span> {detail.discharge_summary ? "Yes" : "Pending"}</p>
              <p><span className="font-medium">Billing:</span> {detail.billing ? `₹${detail.billing.total_amount} (${detail.billing.payment_status})` : "Pending"}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <CollectPaymentModal admission={payFor} open={!!payFor}
                            onClose={() => setPayFor(null)}
                            onDone={load} />
      <DischargeSummaryModal open={!!summaryFor} admissionId={summaryFor}
                              onClose={() => setSummaryFor(null)} />
    </div>
  );
}

// ---------- Tab 2: Ward & Beds ----------
function WardBeds() {
  const [wards, setWards] = useState([]);
  const [beds, setBeds] = useState([]);
  const load = async () => {
    try {
      const [w, b] = await Promise.all([api.get("/ipd/wards"), api.get("/ipd/beds")]);
      setWards(w.data); setBeds(b.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const color = (s) => s === "available" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                    : s === "occupied" ? "bg-rose-100 text-rose-800 border-rose-300"
                    : "bg-amber-100 text-amber-800 border-amber-300";

  return (
    <div className="space-y-4" data-testid="ipd-tab-wards">
      <div className="flex justify-end gap-2">
        <NewWardModal onCreated={load} />
        <NewBedModal onCreated={load} />
        <Button variant="outline" size="sm" onClick={load}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>
      {wards.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-6">
          No wards yet. Click <strong>&quot;New Ward&quot;</strong> above to create your first ward.
        </p>
      )}
      {wards.map((w) => {
        const wardBeds = beds.filter((b) => b.ward_id === w.id);
        const occ = wardBeds.filter((b) => b.status === "occupied").length;
        return (
          <Card key={w.id} className="p-4 space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-heading text-lg font-semibold">{w.ward_name}</h3>
                <p className="text-xs text-slate-500 uppercase">{w.ward_type} · ₹{w.charges_per_bed_per_day}/day</p>
              </div>
              <Badge variant="outline">{occ}/{w.total_beds} occupied</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {wardBeds.map((b) => (
                <div key={b.id} className={`p-3 rounded-lg border ${color(b.status)}`}
                     data-testid={`ipd-bed-${b.id}`}>
                  <p className="font-mono font-semibold">{b.bed_number}</p>
                  <p className="text-xs uppercase">{b.bed_type}</p>
                  <p className="text-xs mt-1">{b.status}</p>
                </div>
              ))}
              {wardBeds.length === 0 && (
                <p className="col-span-full text-xs text-slate-400">No beds registered in this ward.</p>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ---------- Tab 3: Discharge ----------
function DischargePatient({ onDone }) {
  const [active, setActive] = useState([]);
  const [aid, setAid] = useState("");
  const [diagnosis, setDiag] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [dischargedAid, setDischargedAid] = useState(null);

  useEffect(() => {
    api.get("/ipd/admissions?status=active").then(({ data }) => setActive(data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!aid || !diagnosis) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/ipd/admissions/${aid}/discharge`, {
        discharge_diagnosis: diagnosis, follow_up_instructions: followUp,
      });
      setReceipt(data.billing);
      setDischargedAid(aid);
      toast.success("Patient discharged. Bill generated.");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Discharge failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-lg" data-testid="ipd-tab-discharge">
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Active admission</Label>
          <select value={aid} onChange={(e) => setAid(e.target.value)} required
                  className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm"
                  data-testid="ipd-discharge-select">
            <option value="">Select an admission…</option>
            {active.map((a) => (
              <option key={a.id} value={a.id}>{a.reason_for_admission} · {a.admission_date}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Discharge diagnosis</Label>
          <textarea value={diagnosis} onChange={(e) => setDiag(e.target.value)} required
                    rows={3} className="w-full rounded-md border border-slate-200 p-2 text-sm"
                    data-testid="ipd-discharge-diagnosis" />
        </div>
        <div>
          <Label>Follow-up instructions</Label>
          <textarea value={followUp} onChange={(e) => setFollowUp(e.target.value)}
                    rows={2} className="w-full rounded-md border border-slate-200 p-2 text-sm"
                    data-testid="ipd-discharge-followup" />
        </div>
        <Button type="submit" disabled={busy || !aid || !diagnosis}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                data-testid="ipd-discharge-submit">
          {busy ? "Processing…" : "Discharge patient"}
        </Button>
      </form>
      {receipt && (
        <Card className="p-4 border-emerald-200 bg-emerald-50" data-testid="ipd-discharge-receipt">
          <p className="text-xs uppercase text-emerald-700">Bill generated</p>
          <p className="font-heading text-3xl font-bold text-emerald-900 mt-1">
            ₹{receipt.total_amount?.toLocaleString("en-IN")}
          </p>
          <p className="text-xs text-emerald-700 mt-2">
            Bed: ₹{receipt.bed_charges?.total} · Meds: ₹{receipt.medication_charges} ·
            Procedures: ₹{receipt.procedure_charges} · Consult: ₹{receipt.consultation_charges}
          </p>
          <p className="text-xs text-emerald-700 mt-1">
            LOS: {receipt.bed_charges?.days} day(s) · Status: {receipt.payment_status}
          </p>
          <Button size="sm" variant="outline" onClick={() => setShowSummary(true)}
                  className="mt-3 text-blue-700 border-blue-200 hover:bg-blue-50"
                  data-testid="ipd-print-discharge-summary-btn">
            Print discharge summary
          </Button>
        </Card>
      )}
      <DischargeSummaryModal open={showSummary} admissionId={dischargedAid}
                              onClose={() => setShowSummary(false)} />
    </div>
  );
}

// ---------- Tab 4: Billing ----------
function Billing() {
  const [rows, setRows] = useState([]);
  const [previewId, setPreviewId] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/ipd/admissions");
      const withBilling = [];
      for (const a of data) {
        try {
          const b = await api.get(`/ipd/billing/${a.id}`);
          withBilling.push({ admission: a, bill: b.data });
        } catch { /* no bill yet */ }
      }
      setRows(withBilling);
    } catch (e) { toast.error("Failed to load billing"); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-3" data-testid="ipd-tab-billing">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{rows.length} bill(s)</p>
        <div className="flex gap-2">
          <NewBillingModal onCreated={load} />
          <Button size="sm" variant="outline" onClick={load}><RefreshCw size={14} className="mr-1" />Refresh</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>Doctor</TableHead>
            <TableHead>Ward · Bed</TableHead>
            <TableHead>Days</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Paid</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={8} className="text-center text-slate-400">
              No bills yet. Click <strong>&quot;Create Bill&quot;</strong> to bill an active admission.
            </TableCell></TableRow>
          ) : rows.map(({ admission, bill }) => (
            <TableRow key={bill.id} data-testid={`ipd-bill-row-${bill.id}`}>
              <TableCell className="font-medium">
                {bill.patient_name || admission.patient_name || <span className="text-slate-400">—</span>}
              </TableCell>
              <TableCell>{bill.doctor_name || admission.doctor_name || <span className="text-slate-400">—</span>}</TableCell>
              <TableCell>
                {bill.ward_name || admission.ward_name || "—"}
                {" · "}
                <span className="font-mono text-xs">{bill.bed_number || admission.bed_number || "—"}</span>
              </TableCell>
              <TableCell>{bill.bed_charges?.days}</TableCell>
              <TableCell className="font-semibold">₹{bill.total_amount?.toLocaleString("en-IN")}</TableCell>
              <TableCell>₹{(bill.amount_paid || 0).toLocaleString("en-IN")}</TableCell>
              <TableCell>
                <Badge variant={bill.payment_status === "paid" ? "default"
                              : bill.payment_status === "partial" ? "secondary" : "destructive"}>
                  {bill.payment_status}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="outline"
                        onClick={() => setPreviewId(admission.id)}
                        data-testid={`ipd-view-bill-${bill.id}`}>
                  <Eye size={12} className="mr-1" /> View
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-slate-400">Payment recording is an admin-only action (Hospital Admin dashboard).</p>
      <BillPreviewModal open={!!previewId} admissionId={previewId} onClose={() => setPreviewId(null)} />
    </div>
  );
}

// ---------- Tab 5: Inventory ----------
function Inventory() {
  const [rows, setRows] = useState([]);
  const [restockFor, setRestockFor] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/ipd/inventory"); setRows(data); }
    catch { toast.error("Failed to load inventory"); }
  };
  useEffect(() => { load(); }, []);

  const lowCount = rows.filter((r) => r.low_stock).length;

  return (
    <div className="space-y-3" data-testid="ipd-tab-inventory">
      <div className="flex justify-end gap-2">
        <NewInventoryModal onCreated={load} />
        <Button size="sm" variant="outline" onClick={load}><RefreshCw size={14} className="mr-1" />Refresh</Button>
      </div>
      {lowCount > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-200 text-amber-900 flex items-center gap-2">
          <AlertTriangle size={16} />
          <p className="text-sm">{lowCount} item(s) below reorder level.</p>
        </Card>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {rows.length === 0 ? (
          <p className="col-span-full text-sm text-slate-400 text-center py-6">
            No inventory yet. Click <strong>&quot;New Item&quot;</strong> above to add your first item.
          </p>
        ) : rows.map((r) => (
          <Card key={r.id} className={`p-4 ${r.low_stock ? "border-rose-300 bg-rose-50" : ""}`}
                data-testid={`ipd-inv-${r.id}`}>
            <p className="font-heading font-semibold">{r.item_name}</p>
            <p className="text-xs uppercase text-slate-500">{r.item_category}</p>
            <p className="text-2xl font-bold mt-2">{r.quantity} <span className="text-sm text-slate-500">{r.unit}</span></p>
            <p className="text-xs text-slate-500">reorder ≤ {r.reorder_level}</p>
            <Button size="sm" variant="outline" className="mt-2 w-full"
                    onClick={() => setRestockFor(r)}
                    data-testid={`ipd-restock-${r.id}`}>
              Restock
            </Button>
          </Card>
        ))}
      </div>
      <RestockModal item={restockFor} open={!!restockFor}
                    onClose={() => setRestockFor(null)} onDone={load} />
    </div>
  );
}

// ---------- Main ----------
export default function ReceptionistIPD() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [occ, setOcc] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const perms = user?.permissions || {};
  const master = !!perms.can_manage_ipd;
  const has = (k) => master || !!perms[k];

  // Build the tab list dynamically from sub-permissions so admins get
  // fine-grained control over what the receptionist can see.
  const tabDefs = [
    { id: "admissions", label: "Admissions", icon: Stethoscope, on: has("can_ipd_admissions"),
      render: () => <ActiveAdmissions key={refreshKey} /> },
    { id: "wards",      label: "Wards & Beds", icon: BedDouble,
      on: has("can_ipd_wards") || has("can_ipd_beds"),
      render: () => <WardBeds key={refreshKey} /> },
    { id: "discharge",  label: "Discharge", icon: Building2, on: has("can_ipd_discharge"),
      render: () => <DischargePatient onDone={() => setRefreshKey((k) => k + 1)} /> },
    { id: "billing",    label: "Billing", icon: Receipt, on: has("can_ipd_billing"),
      render: () => <Billing key={refreshKey} /> },
    { id: "inventory",  label: "Inventory", icon: Package, on: has("can_ipd_inventory"),
      render: () => <Inventory key={refreshKey} /> },
    { id: "abdm",       label: "ABHA Consents", icon: ShieldCheck,
      on: has("can_ipd_admissions") || has("can_ipd_discharge"),
      render: () => <ABDMConsentsPanel key={refreshKey} /> },
  ];
  const visibleTabs = tabDefs.filter((t) => t.on);
  const hasAnyIpdAccess = master || visibleTabs.length > 0;

  useEffect(() => {
    if (!hasAnyIpdAccess) return;
    api.get("/ipd/analytics/occupancy").then(({ data }) => setOcc(data)).catch(() => { /* ignore */ });
  }, [hasAnyIpdAccess, refreshKey]);

  if (!hasAnyIpdAccess) {
    return (
      <div className="min-h-screen bg-slate-50 p-6" data-testid="ipd-no-permission">
        <Card className="max-w-lg mx-auto p-6 text-center space-y-3">
          <AlertTriangle className="w-8 h-8 mx-auto text-amber-500" />
          <h1 className="font-heading text-xl font-semibold">Permission required</h1>
          <p className="text-sm text-slate-600">
            You don&apos;t have permission to manage IPD. Please contact your hospital administrator.
          </p>
          <Button onClick={() => nav("/receptionist/dashboard")} variant="outline">
            <ArrowLeft className="mr-1" size={14} /> Back to dashboard
          </Button>
        </Card>
      </div>
    );
  }

  const doLogout = async () => { await logout(); nav("/receptionist/login"); };
  const defaultTab = visibleTabs[0]?.id || "admissions";
  const gridCols =
    visibleTabs.length <= 2 ? "grid-cols-2"
    : visibleTabs.length === 3 ? "grid-cols-3"
    : visibleTabs.length === 4 ? "grid-cols-2 md:grid-cols-4"
    : visibleTabs.length === 5 ? "grid-cols-2 md:grid-cols-5"
    : "grid-cols-2 md:grid-cols-6";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="receptionist-ipd">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/receptionist/dashboard")}
                  className="p-2 rounded hover:bg-slate-100" data-testid="ipd-back-btn">
            <ArrowLeft size={16} />
          </button>
          <div className="rounded-full p-2 bg-blue-100 text-blue-700"><Hospital size={18} /></div>
          <div>
            <p className="font-heading text-lg font-semibold">IPD Management</p>
            <p className="text-xs text-slate-500">
              {user?.full_name} · {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {occ && (
            <div className="hidden md:flex gap-4 items-center text-xs text-slate-500 mr-4">
              <span><strong className="text-slate-800">{occ.total_beds}</strong> beds</span>
              <span><strong className="text-rose-600">{occ.occupied_beds}</strong> occupied</span>
              <span><strong className="text-emerald-600">{occ.available_beds}</strong> free</span>
            </div>
          )}
          <Button variant="ghost" size="sm" onClick={doLogout}><LogOut size={14} className="mr-1" />Logout</Button>
        </div>
      </header>

      <main className="p-4 md:p-8 max-w-6xl mx-auto space-y-4">
        <PendingAdmissionsPanel onChanged={() => setRefreshKey((k) => k + 1)} />
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className={`grid ${gridCols}`}>
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.id} value={t.id} data-testid={`ipd-tab-${t.id}-btn`}>
                  <Icon size={14} className="mr-1" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {visibleTabs.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              <Card className="p-4">{t.render()}</Card>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
